import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ConnectionQuality, RoomEvent } from 'livekit-client'
import { useRoomContext } from '@livekit/components-react'

import { t } from './i18n'
import { useFocusTrap } from './useFocusTrap'
import { report } from './diagnostics'

const CONNECTION_PROMPT_DWELL_MS = 5000

/**
 * Catch a weak LOCAL connection and steer to the in-place fix before the tutor
 * reaches for Zoom.
 *
 * LiveKit's ConnectionQualityChanged fires for EVERY participant; we watch only
 * the local one. It fires on CHANGE, so we also seed from the current value — a
 * client that arrived already-poor would otherwise never hear about it.
 *
 * Independent of useDataSaver on purpose: the saver pauses cameras on poor quality
 * with its own hysteresis, and this is a separate, escapable nudge — not a second
 * owner of that state.
 */
export function useConnectionPrompt(enabled: boolean, credential: { code?: string | null; reconnectToken?: string | null }): { show: boolean; dismiss: () => void } {
  const room = useRoomContext()
  const [show, setShow] = useState(false)
  // Latched on dismiss so a still-poor connection does not immediately re-nag,
  // and cleared once quality recovers so a LATER drop is allowed to prompt again.
  const dismissedRef = useRef(false)
  const lostReportedRef = useRef(false)

  useEffect(() => {
    if (!enabled || !room) return

    let timer: ReturnType<typeof setTimeout> | undefined
    const isPoor = (quality: ConnectionQuality) =>
      quality === ConnectionQuality.Poor || quality === ConnectionQuality.Lost

    const evaluate = (quality: ConnectionQuality) => {
      if (quality === ConnectionQuality.Lost && !lostReportedRef.current) {
        lostReportedRef.current = true
        report(credential, { stage: 'permissions', outcome: 'error', permission: 'link=lost' })
      }
      if (isPoor(quality)) {
        if (!dismissedRef.current && timer === undefined) {
          timer = setTimeout(() => {
            if (!dismissedRef.current && isPoor(room.localParticipant.connectionQuality)) setShow(true)
          }, CONNECTION_PROMPT_DWELL_MS)
        }
      } else {
        clearTimeout(timer)
        timer = undefined
        dismissedRef.current = false
        setShow(false)
      }
    }

    // Seed: quality events fire on change only.
    evaluate(room.localParticipant.connectionQuality)

    const onQuality = (quality: ConnectionQuality, participant: { isLocal: boolean }) => {
      if (participant.isLocal) evaluate(quality)
    }
    room.on(RoomEvent.ConnectionQualityChanged, onQuality)
    return () => {
      clearTimeout(timer)
      room.off(RoomEvent.ConnectionQualityChanged, onQuality)
    }
  }, [credential, enabled, room])

  const dismiss = () => {
    dismissedRef.current = true
    setShow(false)
  }

  return { show, dismiss }
}

/**
 * The dialog. Escapable by design — a weak connection often clears on its own, so
 * this must never trap anyone. It offers the in-place fix and NEVER opens Zoom;
 * moving the class stays a separate, deliberate choice on the header control.
 */
export function ConnectionPrompt({
  recovering,
  onFix,
  onDismiss,
  onOpenSetupHub,
}: {
  /** A recovery is in flight — the Fix button says so and cannot be re-tapped. */
  recovering: boolean
  onFix: () => void
  onDismiss: () => void
  onOpenSetupHub?: () => void
}) {
  const modalRef = useRef<HTMLDivElement>(null)
  const dismissRef = useRef<HTMLButtonElement>(null)
  useFocusTrap(modalRef)

  // Kept current so the Escape handler reads live `onDismiss` without the effect
  // resubscribing each render.
  const dismissHandler = useRef(onDismiss)
  dismissHandler.current = onDismiss

  useEffect(() => {
    dismissRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismissHandler.current()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // Portalled for the same reason ConfirmDialog/FallbackDialog are: the control
  // bar's backdrop-filter would otherwise become the containing block and strand a
  // fixed dialog off-screen.
  return createPortal(
    <div
      className="modal-scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onDismiss()
      }}
    >
      <div
        ref={modalRef}
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="connection-title"
      >
        <div className="modal__icon" aria-hidden="true">
          📶
        </div>
        <h2 className="modal__title" id="connection-title">
          {t('connection.title')}
        </h2>
        <p className="modal__body">{t('connection.body')}</p>
        <div className="modal__actions">
          <button type="button" className="pill-button" onClick={onDismiss} ref={dismissRef}>
            {t('mic.help.notNow')}
          </button>
          <button
            type="button"
            className="primary-button"
            disabled={recovering}
            onClick={() => {
              if (recovering) return
              onFix()
            }}
          >
            {recovering ? t('fallback.fixing') : t('fallback.fixNow')}
          </button>
          {onOpenSetupHub ? <button type="button" className="ghost-button" onClick={onOpenSetupHub}>{t('help.menu')}</button> : null}
        </div>
      </div>
    </div>,
    document.body,
  )
}
